import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { EditDialogService } from '../editDialog/editDialogService';
import { TimeEntryDialogDialogComponent } from '../time-entry-dialog/time-entry-dialog.component';
import { RolesService } from '../../services/roles/roles.service';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';
import { Reloadable } from '../../services/command/command.service';
import { TimeEntryService } from '../../services/timeEntry/timeEntryService';
import { TimeEntryCategorySum } from '../../entities/timeEntryCategorySum/timeEntryCategorySum';

@Component({
  selector: 'app-timeEntry-list',
  templateUrl: './timeEntry-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeEntryListComponent implements OnInit, OnDestroy, Reloadable {

  // Injected services
  private readonly destroyRef = inject(DestroyRef);
  readonly rolesService = inject(RolesService);
  private readonly timeEntryService = inject(TimeEntryService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private readonly editDialogService = inject(EditDialogService);
  private unregisterRefetch?: () => void;
  private dialogRef?: DynamicDialogRef;

  // Inputs
  readonly headline = input<string>();
  readonly ownerId = input<string>();
  readonly ownerType = input<string>();
  readonly emptyMessage = input<string>();

  // State
  readonly categorySums = signal<TimeEntryCategorySum[]>([]);
  readonly totalDuration = signal<number>(0);

  // Computed
  readonly categoryPercentages = computed(() => {
    const total = this.totalDuration();
    if (total === 0) return [];

    return this.categorySums().map(catSum => ({
      categoryId: catSum.category.id,
      categoryName: catSum.category.name,
      categoryColor: catSum.category.color,
      percentage: (catSum.sum / total) * 100
    }));
  });

  formatDurationToStd(seconds: number): string {
    if (!seconds && seconds !== 0) {
      return '00:00 h';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${formattedHours}:${formattedMinutes} h`;
  }

  constructor() {
    effect(() => {
      const id = this.ownerId();
      if (id) {
        this.loadCategorySums();
      }
    });
  }

  ngOnInit(): void {
    const ownerId = this.ownerId();
    const ownerType = this.ownerType();
    if (ownerId && ownerType) {
      this.unregisterRefetch = this.refetchCoordinator.register(
        this,
        ownerType,
        ownerId
      );
    }
  }

  ngOnDestroy(): void {
    if (this.unregisterRefetch) {
      this.unregisterRefetch();
    }
  }

  reload(): void {
    this.loadCategorySums();
  }

  private loadCategorySums(): void {
    this.timeEntryService.getTimeEntryCategoriesSum({
      ownerId: this.ownerId(),
      ownerType: this.ownerType()
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      this.categorySums.set(result);
      // Gesamtsumme berechnen
      const total = result.reduce((sum, catSum) => sum + catSum.sum, 0);
      this.totalDuration.set(total);
    });
  }

  createTimeEntry(): void {
    this.dialogRef = this.editDialogService.open(TimeEntryDialogDialogComponent, {
      header: 'Zeiteintrag hinzufügen',
      additionalClass: 'time-entry-dialog',
      data: {
        taskId: this.ownerType() === 'Task' ? this.ownerId() : undefined,
        ownerId: this.ownerId(),
        ownerType: this.ownerType(),
      }
    });

    this.dialogRef.onClose.subscribe((timeEntryId: string) => {
      if (timeEntryId) {
        this.loadCategorySums();
      }
    });
  }
}
